// Relative (not @schemas) so bun test can resolve it.
import {
	CountryCodeRegex,
	normalizeCountry,
	RegionRegex,
} from "../../../schemas/geo";

// Text formats for the Geo routing editors, one line per region:
//   country map:  "eu-central: AT, DE, FR"
//   fallbacks:    "us-east: us-west, eu-central"
// Blank lines and lines starting with # are ignored.

export function detectedLocationLabel(host: {
	detectedRegion: string | null;
	detectedZone: string | null;
	detectedPool: string | null;
}): string {
	if (!host.detectedRegion) return "no location reported";
	const where = host.detectedZone
		? `${host.detectedRegion} / ${host.detectedZone}`
		: host.detectedRegion;
	return host.detectedPool
		? `detected: ${host.detectedPool} (${where})`
		: `location unknown (${where}), set region manually`;
}

export type ParseResult<T> =
	| { ok: true; value: T }
	| { ok: false; error: string };

function splitLines(text: string) {
	return text
		.split("\n")
		.map((raw, i) => ({ line: raw.trim(), n: i + 1 }))
		.filter(({ line }) => line !== "" && !line.startsWith("#"));
}

function splitEntry(
	line: string,
	n: number,
): ParseResult<{ region: string; items: string[] }> {
	const idx = line.indexOf(":");
	if (idx === -1) {
		return { ok: false, error: `Line ${n}: expected "region: a, b"` };
	}
	const region = line.slice(0, idx).trim().toLowerCase();
	if (!RegionRegex.test(region)) {
		return { ok: false, error: `Line ${n}: invalid region "${region}"` };
	}
	const items = line
		.slice(idx + 1)
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter(Boolean);
	return { ok: true, value: { region, items } };
}

export function formatCountryRegions(map: Record<string, string>): string {
	const byRegion = new Map<string, string[]>();
	for (const [country, region] of Object.entries(map)) {
		const list = byRegion.get(region) ?? [];
		list.push(country);
		byRegion.set(region, list);
	}
	return [...byRegion.entries()]
		.map(([region, countries]) => `${region}: ${countries.join(", ")}`)
		.join("\n");
}

export function parseCountryRegions(
	text: string,
): ParseResult<Record<string, string>> {
	const out: Record<string, string> = {};
	for (const { line, n } of splitLines(text)) {
		const entry = splitEntry(line, n);
		if (!entry.ok) return entry;
		for (const raw of entry.value.items) {
			const country = normalizeCountry(raw);
			if (!CountryCodeRegex.test(country)) {
				return { ok: false, error: `Line ${n}: invalid country "${raw}"` };
			}
			const prev = out[country];
			if (prev && prev !== entry.value.region) {
				return {
					ok: false,
					error: `Line ${n}: ${country} is already mapped to ${prev}`,
				};
			}
			out[country] = entry.value.region;
		}
	}
	return { ok: true, value: out };
}

export function formatRegionFallbacks(map: Record<string, string[]>): string {
	return Object.entries(map)
		.map(([region, list]) => `${region}: ${list.join(", ")}`)
		.join("\n");
}

export function parseRegionFallbacks(
	text: string,
): ParseResult<Record<string, string[]>> {
	const out: Record<string, string[]> = {};
	for (const { line, n } of splitLines(text)) {
		const entry = splitEntry(line, n);
		if (!entry.ok) return entry;
		const { region, items } = entry.value;
		if (out[region]) {
			return { ok: false, error: `Line ${n}: ${region} is listed twice` };
		}
		const list: string[] = [];
		for (const raw of items) {
			const fb = raw.toLowerCase();
			if (!RegionRegex.test(fb)) {
				return { ok: false, error: `Line ${n}: invalid region "${raw}"` };
			}
			if (fb !== region && !list.includes(fb)) list.push(fb);
		}
		out[region] = list;
	}
	return { ok: true, value: out };
}
